import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import NotAvailable from '../Components/NotAvailable';
import { getClient, listClientAudit, useNonMmlAdminAccess } from '../utils/oidcClients';
import { idOf } from '../utils/partnerInquiries';

const ACTION_LABEL = {
    created: 'Created',
    updated: 'Edited',
    revoked: 'Revoked',
    secret_rotated: 'Secret rotated',
    // api#756: migration 007 re-encrypting a stored secret, and a refused write.
    secret_encrypted: 'Secret encrypted',
    denied: 'Refused',
};

// What a refused write was trying to do (api#756 `changes.attempted`).
const ATTEMPTED_LABEL = {
    create: 'register a client',
    update: 'edit',
    revoke: 'revoke',
    rotate_secret: 'rotate the secret',
};

// The API's maximum page size for the audit endpoint.
const AUDIT_LIMIT = 100;

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '—');

const formatValue = (value) => {
    if (value === null || value === undefined) return '(none)';
    return Array.isArray(value) ? value.join(', ') : String(value);
};

// A refused write carries { attempted, reason, httpStatus } instead of
// before/after (api#756), e.g. "Tried to edit, refused (409): …".
function DeniedSummary({ changes = {} }) {
    const attempted = ATTEMPTED_LABEL[changes.attempted] || changes.attempted || 'change this client';
    const status = changes.httpStatus ? ` (${changes.httpStatus})` : '';
    return (
        <div className="f_sz13">
            Tried to {attempted}, refused{status}{changes.reason ? `: ${changes.reason}` : ''}
        </div>
    );
}

// A refusal changed nothing; set it apart from the real changes.
const rowClassName = (entry) => (entry.action === 'denied' ? 'table-warning' : undefined);

// One line per changed field, e.g. "name: Pulse → Pulse Health".
function ChangeSummary({ entry }) {
    if (entry.action === 'denied') return <DeniedSummary changes={entry.changes} />;
    const before = entry.changes?.before || {};
    const after = entry.changes?.after || {};
    const fields = Object.keys(after);
    if (entry.action === 'secret_rotated' || entry.action === 'secret_encrypted' || fields.length === 0) {
        return <span className="text-muted">—</span>;
    }
    return (
        <>
            {fields.map((field) => (
                <div key={field} className="f_sz13">
                    <strong>{field}</strong>:{' '}
                    {field in before ? `${formatValue(before[field])} → ` : ''}
                    {formatValue(after[field])}
                </div>
            ))}
        </>
    );
}

// admin#175 — one partner client and its permanent audit history.
function OidcClientsView() {
    const { clientId } = useParams();
    const allowed = useNonMmlAdminAccess();
    const [client, setClient] = useState(null);
    const [clientError, setClientError] = useState('');
    const [audit, setAudit] = useState([]);
    const [auditTotal, setAuditTotal] = useState(0);
    const [auditError, setAuditError] = useState('');
    const [loading, setLoading] = useState(true);

    // Loaded independently: the audit read can fail on its own (not deployed
    // yet, a 403, the audit store down), and that mustn't hide the client or
    // be shown as "no history".
    useEffect(() => {
        if (!allowed) return;
        (async () => {
            const [clientResult, auditResult] = await Promise.allSettled([
                getClient(clientId),
                listClientAudit(clientId, { limit: AUDIT_LIMIT }),
            ]);
            if (clientResult.status === 'fulfilled') {
                setClient(clientResult.value);
            } else {
                setClientError(clientResult.reason?.message || 'Failed to load client.');
            }
            if (auditResult.status === 'fulfilled') {
                const entries = auditResult.value?.entries || [];
                setAudit(entries);
                setAuditTotal(auditResult.value?.pagination?.total ?? entries.length);
            } else {
                setAuditError(auditResult.reason?.message || 'Failed to load history.');
            }
            setLoading(false);
        })();
    }, [allowed, clientId]);

    if (allowed === false) return <NotAvailable />;

    const rows = client ? [
        ['Name', client.name],
        ['Client ID', <code key="id">{client.clientId}</code>],
        ['Status', client.status],
        ['Type', client.isPublicClient ? 'Public (no secret)' : 'Confidential'],
        ['First party', client.isFirstParty ? 'Yes' : 'No'],
        ['Redirect URIs', (client.redirectUris || []).map((u) => <div key={u}>{u}</div>)],
        ['Scopes', (client.allowedScopes || []).join(' ')],
        ['Grant types', (client.grantTypes || []).join(', ')],
        // admin#181 — the inquiry this client was registered for, if any.
        ...(idOf(client.partnerContactId) ? [['Partner inquiry', (
            <Link key="inquiry" to={`/partnerInquiriesView/${encodeURIComponent(idOf(client.partnerContactId))}`}>
                View inquiry
            </Link>
        )]] : []),
        ['Created', formatDateTime(client.createdAt)],
        ['Last updated', formatDateTime(client.updatedAt)],
    ] : [];

    return (
        <>
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">{client?.name || 'Partner Client'}</h1>
                        </div>
                        <div className="col-sm-6 txt_rgt">
                            {client && client.status !== 'revoked' && (
                                <Link to={`/oidcClientsAdd/${encodeURIComponent(clientId)}`} className="btn btn-blue mr-2">
                                    Edit
                                </Link>
                            )}
                            <Link to="/oidcClients" className="btn btn-secondary">Back</Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    {loading ? (
                        <div className="text-center p-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    ) : clientError ? (
                        <div className="alert alert-danger txt_lft" role="alert">
                            Couldn&apos;t load this client: {clientError}{' '}
                            <Link to="/oidcClients">Back to Partner Clients</Link>
                        </div>
                    ) : (
                        <>
                            <div className="card mb-3">
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table width="100%" className="table table-striped table-bordered txt_lft">
                                            <tbody>
                                                {rows.map(([label, value]) => (
                                                    <tr key={label}>
                                                        <td style={{ width: '200px' }}><span className="fwt_600">{label}</span></td>
                                                        <td>{value}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div className="card mb-3">
                                <div className="card-body">
                                    <h5 className="mb-3 txt_lft">History</h5>
                                    {auditError ? (
                                        <div className="alert alert-warning txt_lft" role="alert">
                                            Couldn&apos;t load the history: {auditError}
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            {auditTotal > audit.length && (
                                                <p className="text-muted f_sz13 txt_lft">
                                                    Showing the latest {audit.length} of {auditTotal} entries.
                                                </p>
                                            )}
                                            <table width="100%" className="table table-striped table-bordered txt_lft">
                                                <thead>
                                                    <tr>
                                                        <th>When</th>
                                                        <th>Action</th>
                                                        <th>By</th>
                                                        <th>Changes</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {audit.length === 0 && (
                                                        <tr>
                                                            <td colSpan="4" className="text-center text-muted">
                                                                No history recorded. Changes made before the audit log
                                                                existed aren&apos;t listed.
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {audit.map((entry) => (
                                                        <tr key={entry._id} className={rowClassName(entry)}>
                                                            <td className="text-nowrap">{formatDateTime(entry.createdAt)}</td>
                                                            <td>{ACTION_LABEL[entry.action] || entry.action}</td>
                                                            <td>{entry.actorEmail || '—'}</td>
                                                            <td><ChangeSummary entry={entry} /></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default OidcClientsView;
